// The user's default Transcribe/Dictate mode, chosen while offline and not yet
// PATCHed to `/user`.
//
// Picking a mode offline writes the *per-session* value straight away (the
// offline bridge persists `dictation_type` on the local row), but persisting the
// user's DEFAULT is a `/user` PATCH, which `handleValueChange` deliberately
// skips offline under the offline read-only policy. Nothing wrote it locally
// instead, so the account default stayed at whatever was last chosen ONLINE — and
// `buildOfflineCreateArgs` reads that default, so every subsequently created
// offline session inherited the stale mode. In practice that meant a clinician
// who picked Transcribe offline kept getting Dictate sessions.
//
// This atom is the local stand-in for the un-PATCHable default. It is read as an
// overlay by `resolveLastUsedRecorderSettings`, so it wins over
// `session_settings.last_recording_mode` on both the live user object and the
// cached snapshot, and it is drained by `useReplayPendingTranscribeMode` on the
// reconnect edge — the same edge and the same drain-then-clear shape as the
// per-session pending store beside it.
//
// Unlike `pendingTranscribeModeBySessionIdAtom` this one IS persisted. That
// store protects an in-memory optimistic cache write which dies with the
// process anyway; this one has to survive an offline relaunch, because the
// stale value it is correcting (`last_recording_mode`, on the persisted user
// snapshot) survives one too. The same class of loss is what made the mode
// reset on a cold offline boot before that was fixed.

import { getDefaultStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { getSafeLocalStorage } from '@/lib/jotai-storage';
import { type TranscribeMode } from '@heidi/schema/ml-scribe';

import { createOfflineStorageFailureReporter } from './storage-failure-reporter';

/**
 * Cleared on full logout by `clearPendingUserDefaultTranscribeMode()` below —
 * the atom write is what the storage row alone never did, and it is not
 * optional for this key. It is unscoped, and it does NOT self-heal
 * on a user switch: this value is an overlay that deliberately OUTRANKS the
 * live user object. Left behind, clinician A's offline choice would drive
 * clinician B's offline sessions and then be PATCHed onto B's account by the
 * reconnect replay.
 */
// UNCARVED DOMAIN KEY (offline-recording, DSK-675): offline recording has no
// workspace package, so there is no /contracts door to declare this in. Not a
// query cache identity — a browser-storage key, registered in
// persistence-manifest.json and cleared on sign-out by the function at the
// bottom of this file, exactly as the sibling offline keys are.
export const PENDING_USER_DEFAULT_TRANSCRIBE_MODE_STORAGE_KEY =
  'heidi.offlineRecording.pendingUserDefaultTranscribeMode';

/**
 * Every refused storage OPERATION on this key — a read, a write, or the
 * sign-out removal that throws once the store is in hand — is reported through
 * here. A browser that refuses access to `window.localStorage` itself is a
 * different case the shared wrapper absorbs before any handler is consulted;
 * see the scope note on the reporter module.
 *
 * The safe wrapper still SWALLOWS the throw: a refused write must not break a
 * render, and this key's removal runs partway through `clearLogoutStorage`,
 * where an escaping throw would abort the rest of the sign-out clear. What the
 * reporter adds is that the failure stops being invisible. Silence is the right
 * call for a preference whose later step matters more than its persistence; it
 * is the wrong call here, because this value is read back by a non-reactive
 * getter on a cold offline boot, which is the one case where no `/user`
 * response arrives to heal it.
 *
 * Constructed with `getSafeLocalStorage(reporter)` rather than the argument-free
 * `createSafeJotaiLocalStorage` helper — the reporter seam is on the wrapper
 * itself, and threading an optional handler through the helper would reshape a
 * signature shared with every other consumer.
 */
const reportStorageFailure = createOfflineStorageFailureReporter(
  'pendingUserDefaultTranscribeModeAtom',
  'localStorage'
);

/**
 * The mode the clinician chose while offline, awaiting its `/user` PATCH.
 * `null` means "nothing pending" — the server-backed default is current.
 */
export const pendingUserDefaultTranscribeModeAtom =
  atomWithStorage<TranscribeMode | null>(
    PENDING_USER_DEFAULT_TRANSCRIBE_MODE_STORAGE_KEY,
    null,
    createJSONStorage<TranscribeMode | null>(() =>
      getSafeLocalStorage(reportStorageFailure)
    ),
    { getOnInit: true }
  );

pendingUserDefaultTranscribeModeAtom.debugLabel = 'pendingUserDefaultTranscribeModeAtom';

/**
 * Non-reactive read, for the offline create path — `buildOfflineCreateArgs`
 * runs inside a `mutationFn`, outside React.
 */
export const getPendingUserDefaultTranscribeMode = (): TranscribeMode | null =>
  getDefaultStore().get(pendingUserDefaultTranscribeModeAtom);

/**
 * Non-reactive write, skipping no-op writes to avoid localStorage churn.
 *
 * Accepts an updater as well as a value. Callers that clear the store are
 * resolving a race — a PATCH settling against a choice the clinician may have
 * changed while it was in flight — and they need to read the current value at
 * settle time, not at call time.
 */
export const setPendingUserDefaultTranscribeMode = (
  next: TranscribeMode | null | ((prev: TranscribeMode | null) => TranscribeMode | null)
): void => {
  const store = getDefaultStore();
  const current = store.get(pendingUserDefaultTranscribeModeAtom);
  const resolved = typeof next === 'function' ? next(current) : next;
  if (current === resolved) {
    return;
  }
  store.set(pendingUserDefaultTranscribeModeAtom, resolved);
};

/**
 * Drop the pending offline mode choice. Called from `clearLogoutStorage`.
 *
 * Two writes, because either one on its own is a half-fix:
 *
 * - The atom is emptied, because removing the storage row behind a live storage
 *   atom leaves memory and storage disagreeing. Its only subscription is the
 *   `storage` window event, which does not fire in the tab that made the
 *   change, so the atom would go on serving the departing clinician's mode —
 *   and `getPendingUserDefaultTranscribeMode` reads the ATOM, so the reader
 *   this key exists for would never see the removal. It would not even heal on
 *   a later reload, because the next write re-persists what the atom still
 *   holds.
 * - The storage row is removed, because emptying the atom writes `null` back
 *   through the adapter, which persists the string `"null"` rather than
 *   clearing the row. So the removal must come SECOND.
 *
 * Both halves go through the SAFE storage wrappers, which swallow a throwing
 * `Storage`. A restricted webview can refuse storage access outright, and this
 * runs partway through `clearLogoutStorage` — an escaping throw here would
 * abort the clear beside it and the whole `LOGOUT_KEYS` loop below, so a
 * sign-out that could not write would stop clearing anything at all. Both also
 * carry the reporter above, so a sign-out that could not clear this key says so
 * instead of passing silently.
 *
 * The key stays on `LOGOUT_KEYS` as the registry entry every persisted
 * user-derived key is required to have, and as a backstop if this call site is
 * ever dropped — but that list is a `localStorage.removeItem` loop, so it only
 * ever did the second half. A row there reading as coverage while the live
 * overlay survived IS the defect this replaces.
 *
 * A returning clinician loses a mode choice they made offline and had not yet
 * reconnected to PATCH. That is the intended trade, and it matches the sibling
 * offline preferences on this path.
 */
export const clearPendingUserDefaultTranscribeMode = (): void => {
  getDefaultStore().set(pendingUserDefaultTranscribeModeAtom, null);
  getSafeLocalStorage(reportStorageFailure).removeItem(
    PENDING_USER_DEFAULT_TRANSCRIBE_MODE_STORAGE_KEY
  );
};
